import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface FeatureFlag {
  key: string;
  enabled: boolean;
  scope: string;
  roles_allowed: string[];
}

let cachedFlags: FeatureFlag[] | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 60_000; // 60 seconds

async function fetchFlags(): Promise<FeatureFlag[]> {
  const now = Date.now();
  if (cachedFlags && now - cacheTimestamp < CACHE_TTL) {
    return cachedFlags;
  }

  const { data, error } = await supabase.rpc("get_feature_flags");
  if (error) {
    console.error("[feature-flags] fetch error:", error.message);
    // Return cached if available, otherwise all-enabled fallback
    return cachedFlags ?? [];
  }

  cachedFlags = (data as FeatureFlag[]) ?? [];
  cacheTimestamp = now;
  return cachedFlags;
}

/**
 * Returns whether a feature flag is enabled.
 * For scope='role' flags, pass the user's role to check roles_allowed.
 */
export function useFeatureFlag(flagKey: string, userRole?: string) {
  const { data: flags = [], isLoading } = useQuery({
    queryKey: ["feature-flags"],
    queryFn: fetchFlags,
    staleTime: CACHE_TTL,
    gcTime: CACHE_TTL * 2,
    refetchOnWindowFocus: false,
  });

  const flag = flags.find((f) => f.key === flagKey);

  if (!flag) return { enabled: true, isLoading }; // default: enabled if flag doesn't exist

  if (!flag.enabled) return { enabled: false, isLoading };

  if (flag.scope === "role" && userRole) {
    return { enabled: flag.roles_allowed.includes(userRole), isLoading };
  }

  return { enabled: flag.enabled, isLoading };
}

/** Bulk hook — returns the full flags map */
export function useFeatureFlags() {
  return useQuery({
    queryKey: ["feature-flags"],
    queryFn: fetchFlags,
    staleTime: CACHE_TTL,
    gcTime: CACHE_TTL * 2,
    refetchOnWindowFocus: false,
  });
}

/** Imperative check (for use outside React) */
export function isFeatureEnabled(flagKey: string, userRole?: string): boolean {
  if (!cachedFlags) return true; // default enabled before first fetch
  const flag = cachedFlags.find((f) => f.key === flagKey);
  if (!flag) return true;
  if (!flag.enabled) return false;
  if (flag.scope === "role" && userRole) {
    return flag.roles_allowed.includes(userRole);
  }
  return flag.enabled;
}
